import React, { useState } from "react";
import { ANIMAL_LIST } from "../../constants";
import { useGame } from "../../context";
import "./Login.css";

// 9個密碼關鍵字（動物表情）
const PASSWORD_KEYWORDS = [
  { id: 1, emoji: "🐶", name: "狗" },
  { id: 2, emoji: "🐱", name: "貓" },
  { id: 3, emoji: "🐰", name: "兔" },
  { id: 4, emoji: "🐼", name: "熊貓" },
  { id: 5, emoji: "🦊", name: "狐狸" },
  { id: 6, emoji: "🦁", name: "獅子" },
  { id: 7, emoji: "🐸", name: "青蛙" },
  { id: 8, emoji: "🐵", name: "猴子" },
  { id: 9, emoji: "🐷", name: "豬" },
];

const Login = () => {
  const [username, setUsername] = useState("");
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedAnimal, setSelectedAnimal] = useState(0);
  const { actions } = useGame();

  // 動畫輪換動物
  React.useEffect(() => {
    const interval = setInterval(() => {
      setSelectedAnimal((prev) => (prev + 1) % ANIMAL_LIST.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // 處理關鍵字選擇
  const handleKeywordClick = (keyword) => {
    if (selectedKeywords.length < 4) {
      const newKeywords = [...selectedKeywords, keyword];
      setSelectedKeywords(newKeywords);

      // 自動提交：當選擇了4個關鍵字時
      if (newKeywords.length === 4) {
        // 延遲一點執行，讓UI先更新顯示第4個選擇
        setTimeout(() => {
          autoSubmit(newKeywords);
        }, 300);
      }
    }
  };

  // 自動提交登入
  const autoSubmit = async (keywords) => {
    setError("");
    setSuccessMessage("");

    if (!username.trim()) {
      setError("請輸入你的名字 😊");
      setSelectedKeywords([]);
      return;
    }

    setIsLoading(true);
    const password = keywords.map((k) => k.id).join("");

    try {
      const result = await actions.login(username.trim(), password);

      if (result.success) {
        if (result.isNewUser) {
          setSuccessMessage("🎉 歡迎新玩家！已自動創建帳戶");
        }
        // Login successful, GameContext will update state
      } else {
        setError(
          result.error ||
            "密碼錯誤 😔 此名稱可能已被其他人使用，請換一個名字試試"
        );
        setSelectedKeywords([]); // 密碼錯誤自動清空
      }
    } catch (err) {
      setError("連接失敗，請檢查網絡 😔");
      setSelectedKeywords([]); // 連接失敗也清空
    } finally {
      setIsLoading(false);
    }
  };

  // 刪除最後一個選擇的關鍵字
  const handleDeleteLast = () => {
    setSelectedKeywords(selectedKeywords.slice(0, -1));
  };

  // 清空所有選擇
  const handleClearAll = () => {
    setSelectedKeywords([]);
  };

  // 將關鍵字轉換為密碼（使用ID組合）
  const getPasswordFromKeywords = () => {
    return selectedKeywords.map((k) => k.id).join("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!username.trim()) {
      setError("請輸入你的名字 😊");
      return;
    }

    if (selectedKeywords.length < 4) {
      setError("請選擇4個動物密碼 🔢");
      return;
    }

    setIsLoading(true);
    const password = getPasswordFromKeywords();

    try {
      const result = await actions.login(username.trim(), password);

      if (result.success) {
        if (result.isNewUser) {
          setSuccessMessage("🎉 歡迎新玩家！已自動創建帳戶");
        }
        // Login successful, GameContext will update state
      } else {
        setError(
          result.error ||
            "密碼錯誤 😔 此名稱可能已被其他人使用，請換一個名字試試"
        );
        setSelectedKeywords([]); // 密碼錯誤自動清空
      }
    } catch (err) {
      setError("連接失敗，請檢查網絡 😔");
      setSelectedKeywords([]); // 連接失敗也清空
    } finally {
      setIsLoading(false);
    }
  };

  const currentAnimal = ANIMAL_LIST[selectedAnimal];

  return (
    <div className="login-container">
      <div className="login-background">
        {ANIMAL_LIST.map((animal, index) => (
          <span
            key={animal.id}
            className="floating-animal"
            style={{
              left: `${(index * 15) % 90}%`,
              top: `${(index * 12) % 80}%`,
              animationDelay: `${index * 0.5}s`,
            }}
          >
            {animal.emoji}
          </span>
        ))}
      </div>

      <div className="login-card">
        <div className="login-header">
          <div className="mascot-container">
            <span className="mascot-emoji">{currentAnimal.emoji}</span>
            <div className="mascot-speech">
              <span>歡迎來到數學動物園！</span>
            </div>
          </div>
          <h1 className="login-title">
            <span className="title-emoji">🎮</span>
            數學動物園
            <span className="title-emoji">🦁</span>
          </h1>
          <p className="login-subtitle">和可愛動物一起學數學吧！</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="input-group">
            <label htmlFor="username">
              <span className="input-icon">👤</span>
              你的名字
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="輸入你的名字..."
              className="login-input"
              maxLength={20}
            />
          </div>

          <div className="input-group">
            <label>
              <span className="input-icon">🔐</span>
              選擇4個動物密碼
            </label>

            {/* 已選擇的密碼顯示 */}
            <div className="password-display">
              <div className="selected-keywords">
                {[0, 1, 2, 3].map((index) => (
                  <div
                    key={index}
                    className={`keyword-slot ${
                      selectedKeywords[index] ? "filled" : ""
                    }`}
                  >
                    {selectedKeywords[index] ? selectedKeywords[index].id : "?"}
                  </div>
                ))}
              </div>
              <div className="password-actions">
                <button
                  type="button"
                  className="action-btn delete-btn"
                  onClick={handleDeleteLast}
                  disabled={selectedKeywords.length === 0}
                >
                  ⌫
                </button>
                <button
                  type="button"
                  className="action-btn clear-btn"
                  onClick={handleClearAll}
                  disabled={selectedKeywords.length === 0}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* 9宮格關鍵字選擇 */}
            <div className="keyword-grid">
              {PASSWORD_KEYWORDS.map((keyword) => (
                <button
                  key={keyword.id}
                  type="button"
                  className="keyword-btn"
                  onClick={() => handleKeywordClick(keyword)}
                  disabled={selectedKeywords.length >= 4}
                >
                  <span className="keyword-emoji">{keyword.emoji}</span>
                  <span className="keyword-number">{keyword.id}</span>
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {successMessage && (
            <div
              className="success-message"
              style={{
                backgroundColor: "#d4edda",
                color: "#155724",
                padding: "10px 15px",
                borderRadius: "8px",
                marginBottom: "15px",
                textAlign: "center",
                fontSize: "14px",
              }}
            >
              {successMessage}
            </div>
          )}

          <button type="submit" className="login-button" disabled={isLoading}>
            {isLoading ? (
              <>
                <span className="button-text">登入中...</span>
                <span className="button-icon">⏳</span>
              </>
            ) : (
              <>
                <span className="button-text">開始遊戲</span>
                <span className="button-icon">🚀</span>
              </>
            )}
          </button>

          <div
            className="login-hint"
            style={{
              marginTop: "15px",
              fontSize: "12px",
              color: "#666",
              textAlign: "center",
            }}
          >
            💡 提示：首次登入會自動創建帳戶，請記住你的動物密碼！
          </div>
        </form>

        <div className="login-footer">
          <div className="game-preview">
            <span className="preview-item">🧮 數字分解</span>
            <span className="preview-item">📊 十進位學習</span>
            <span className="preview-item">🔢 倍數規律</span>
          </div>
        </div>
      </div>

      <div className="decorative-elements">
        <span className="star star-1">⭐</span>
        <span className="star star-2">🌟</span>
        <span className="star star-3">✨</span>
        <span className="cloud cloud-1">☁️</span>
        <span className="cloud cloud-2">☁️</span>
      </div>
    </div>
  );
};

export default Login;
